import React, {
  memo,
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { toast } from "react-toastify";
import { ColDef, RowSelectionOptions } from "ag-grid-enterprise";
import { CircleX, FileText, FolderOpen, Users } from "lucide-react";
import QuickSearch from "../../components/QuickSearch";
import { sideBar } from "../../utils/AgGridConfig";
import {
  useGenerateMultipleExcel,
  useGetAllCompletedOrders,
  usePublishCompanies,
} from "../../queries/onboarding";
import {
  CompanyIdRender,
  CompanyNameRender,
  IsPublishedRender,
  PublishedDateRender,
  StatusRender,
  SuggestedMgt7FileRender,
  SuggestedPas3FileRender,
  TagsRender,
} from "./GenerateCompanyExcelAGComponents";
import { CompanyFilesPopup } from "../FileExplorer/FileExplorerPopup";
import { CompanyDetailsPopup } from "../FileExplorer/CompanyDetailsPopup";
import { ShareholderDetailsPopup } from "../FileExplorer/ShareholderDetailsPopup";
import { Action, CTADropdown } from "../../components/Dropdown";
import { FinalDocumentsPopup } from "../FileExplorer/FinalDocumentsPopup";
import { LoaderText } from "../../utils/InputField";
import { ButtonSize } from "../../components/CTAButtonComponents";
import { formatDate } from "../../utils/formatDateDisplay";

export const GenerateCompanyExcelTable: React.FC = () => {
  const gridApi = useRef<any>(null);

  const { data } = useGetAllCompletedOrders();
  const {
    mutate: GenerateBulkExcel,
    isSuccess,
    isError,
  } = useGenerateMultipleExcel();

  const {
    mutate: PublishBulkCompanies,
    isSuccess: isPublishSuccess,
    isError: isPublishError,
  } = usePublishCompanies();

  const [showFilesPopup, setShowFilesPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [isPublishLoading, setIsPublishLoading] = useState(false);
  const [showCompanyDetailsPopup, setShowCompanyDetailsPopup] = useState(false);
  const [showShareholderPopup, setShowShareholderPopup] = useState(false);
  const [showDocumentsPopup, setShowDocumentsPopup] = useState(false);
  const [isPas3File, setIsPas3File] = useState(false);
  const [selectedCin, setSelectedCin] = useState<string>();
  const [selectedRowData, setSelectedRowData] = useState<any>();
  const [companyFiles, setCompanyFiles] = useState<
    { cin: string; pas3?: string; mgt7?: string }[]
  >([]);

  useEffect(() => {
    setIsLoading(false);
  }, [isSuccess, isError]);

  useEffect(() => {
    setIsPublishLoading(false);
  }, [isPublishSuccess, isPublishError]);

  const updateCompanyFile = useCallback(
    (cin: string, field: "pas3" | "mgt7", value?: string) => {
      setCompanyFiles((prevFiles) => {
        const existingIndex = prevFiles.findIndex((file) => file.cin === cin);
        if (existingIndex !== -1) {
          const newFiles = [...prevFiles];
          newFiles[existingIndex] = {
            ...newFiles[existingIndex],
            [field]: value,
          };
          return newFiles;
        } else {
          return [...prevFiles, { cin, [field]: value }];
        }
      });
    },
    [],
  );

  const getCompanyFile = (cin: string) => {
    return companyFiles.find((file) => file.cin === cin);
  };

  function findInvalidCompanies(companies: any[]) {
    return companies?.filter(
      (company) => company.mgt7 === undefined || company.pas3 === undefined,
    );
  }
  function findCompletedCompanies(companies: any[]) {
    return companies?.filter((company) => company.status !== "COMPLETED");
  }

  const getFilesOfCin = (cin: string) => {
    const companyFile = companyFiles.find((file) => file.cin === cin);
    if (companyFile) {
      return {
        pas3: companyFile.pas3,
        mgt7: companyFile.mgt7,
      };
    }
    return {
      pas3: undefined,
      mgt7: undefined,
    };
  };

  function Pas3FileRender(data: any) {
    const companyFile = getCompanyFile(data.data.cin);
    const pas3 = companyFile?.pas3;

    return (
      <div className="mr-1">
        {pas3 ? (
          <div className="relative inline-block max-w-[250px] w-full align-middle">
            <p
              className="text-gray-600 text-sm border border-gray-300 px-2 py-1 pr-6 rounded truncate w-full"
              title={pas3.split("/").pop()}
            >
              {pas3.split("/").pop()}
            </p>
            <button
              onClick={() =>
                updateCompanyFile(data.data.cin, "pas3", undefined)
              }
              className="absolute -top-1 -right-2 text-xs text-red-500 rounded-full px-1 leading-none"
            >
              <CircleX
                height={12}
                width={12}
                className="bg-red-100 rounded-lg"
              />
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              setSelectedCin(data.data.cin);
              setShowFilesPopup(true);
              setIsPas3File(true);
            }}
            className="px-4 py-1 text-sm text-blue-600 border border-blue-500 hover:bg-blue-200 rounded"
          >
            Select PAS-3
          </button>
        )}
      </div>
    );
  }

  function CompanyDetailsRender(data: any) {
    return data.data.status === "COMPLETED" ? (
      <button
        onClick={() => {
          setSelectedCin(data.data.cin);
          setShowCompanyDetailsPopup(true);
        }}
        className="flex items-center gap-1 text-sm mt-2 text-blue-600 underline hover:text-blue-800 bg-transparent border-none p-0 m-0"
      >
        <FileText className="w-4 h-4" />
        View Details
      </button>
    ) : (
      <p className="font-medium truncate"> {"NA"}</p>
    );
  }
  function FinalDocumentsRender(data: any) {
    return data.data.status === "COMPLETED" ? (
      <button
        onClick={() => {
          setSelectedCin(data.data.cin);
          setShowDocumentsPopup(true);
        }}
        className="flex items-center gap-1 text-sm mt-2 text-blue-600 underline hover:text-blue-800 bg-transparent border-none p-0 m-0"
      >
        <FolderOpen className="w-4 h-4" />
        Documents
      </button>
    ) : (
      <p className="font-medium truncate"> {"-"}</p>
    );
  }

  function ShareholderDetailsRender(data: any) {
    return data.data.status === "COMPLETED" ? (
      <button
        onClick={() => {
          setSelectedCin(data.data.cin);
          setShowShareholderPopup(true);
        }}
        className="flex items-center gap-1 text-sm mt-2 text-blue-600 underline hover:text-blue-800 bg-transparent border-none p-0 m-0"
      >
        <Users className="w-4 h-4" />
        <span>Shareholders</span>
      </button>
    ) : (
      <p className="font-medium truncate"> {"NA"}</p>
    );
  }

  function Mgt7FileRender(data: any) {
    const companyFile = getCompanyFile(data.data.cin);
    const mgt7 = companyFile?.mgt7;
    return (
      <div className="mr-1">
        {mgt7 ? (
          <div className="relative inline-block max-w-[250px] w-full align-middle">
            <p
              className="text-gray-600 text-sm border border-gray-300 px-2 py-1 pr-6 rounded truncate w-full"
              title={mgt7.split("/").pop()}
            >
              {mgt7.split("/").pop()}
            </p>
            <button
              onClick={() =>
                updateCompanyFile(data.data.cin, "mgt7", undefined)
              }
              className="absolute -top-1 -right-2 text-xs text-red-500 rounded-full px-1 leading-none"
            >
              <CircleX
                height={12}
                width={12}
                className="bg-red-100 rounded-lg"
              />
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              setSelectedCin(data.data.cin);
              setShowFilesPopup(true);
              setIsPas3File(false);
            }}
            className="px-4 py-1 text-sm text-blue-600 border border-blue-500 hover:bg-blue-200 rounded"
          >
            Select MGT-7
          </button>
        )}
      </div>
    );
  }

  function SubmitButtonRender(data: any) {
    const companyFile = getCompanyFile(data.data.cin);
    const isEnable = companyFile?.pas3 && companyFile?.mgt7;
    return (
      <div>
        <button
          onClick={() => {}}
          className={`px-2 py-1 text-sm text-white ${!isEnable ? "cursor-not-allowed bg-blue-300" : "cursor-pointer bg-blue-500 hover:bg-blue-700"} rounded text-center`}
        >
          Generate Excel
        </button>
      </div>
    );
  }

  const componentsRegistry = useMemo(
    () => ({
      companyName: memo(CompanyNameRender),
      status: memo(StatusRender),
      isPublished: memo(IsPublishedRender),
      tags: memo(TagsRender),
      suggestedPas3File: memo(SuggestedPas3FileRender),
      suggestedMgt7File: memo(SuggestedMgt7FileRender),
      publishedDate: memo(PublishedDateRender),
      publishedCompanyId: memo(CompanyIdRender),
    }),
    [],
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Company Name",
        field: "companyName",
        filter: "agTextColumnFilter",
        sortable: true,
        minWidth: 300,
        cellRenderer: "companyName",
        tooltipField: "companyNameTooltip",
      },
      {
        headerName: "CIN",
        field: "cin",
        filter: "agTextColumnFilter",
        sortable: true,
        tooltipField: "cinTooltip",
      },
      {
        headerName: "Status",
        field: "status",
        cellRenderer: "status",
        filter: "agSetColumnFilter",
        sortable: true,
        // hide: true,
      },
      {
        headerName: "Publish Status",
        field: "isPublished",
        cellRenderer: "isPublished",
        filter: "agSetColumnFilter",
        sortable: true,
        // hide: true,
      },
      {
        headerName: "Suggested PAS-3",
        field: "suggestedPas3File",
        minWidth: 175,
        filter: false,
        cellRenderer: "suggestedPas3File",
        tooltipField: "suggestedPas3FileTooltip",
      },
      {
        headerName: "PAS-3 File",
        field: "pas3File",
        minWidth: 175,
        filter: false,
        cellRenderer: Pas3FileRender,
      },
      {
        headerName: "Suggested MGT-7",
        field: "suggestedMgt7File",
        filter: false,
        minWidth: 175,
        cellRenderer: "suggestedMgt7File",
        tooltipField: "suggestedMgt7FileTooltip",
      },
      {
        headerName: "MGT-7 File",
        field: "mgt7File",
        filter: false,
        minWidth: 175,
        cellRenderer: Mgt7FileRender,
      },
      {
        headerName: "Company Details",
        field: "companyDetails",
        minWidth: 175,
        filter: false,
        cellRenderer: CompanyDetailsRender,
      },
      {
        headerName: "Shareholder Details",
        field: "shareholderDetails",
        minWidth: 200,
        filter: false,
        cellRenderer: ShareholderDetailsRender,
      },
      {
        headerName: "Documents",
        field: "documents",
        minWidth: 150,
        filter: false,
        cellRenderer: FinalDocumentsRender,
      },
      {
        headerName: "Published CompanyId",
        field: "publishedCompanyId",
        minWidth: 150,
        filter: "agTextColumnFilter",
        cellRenderer: "publishedCompanyId",
      },
      {
        headerName: "Tags",
        field: "tags",
        minWidth: 200,
        filter: "agSetColumnFilter",
        cellRenderer: "tags",
      },
      {
        headerName: "Published Date",
        field: "publishedDate",
        sortable: true,
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (cellValue == null) {
              return 0;
            }
            const dateParts = cellValue.split("-");
            const day = Number(dateParts[2]);
            const month = Number(dateParts[1]) - 1;
            const year = Number(dateParts[0]);
            const cellDate = new Date(year, month, day);
            if (cellDate < dateFromFilter) {
              return -1;
            } else if (cellDate > dateFromFilter) {
              return 1;
            }
            return 0;
          },
        },
        cellRenderer: "publishedDate",
      },
      // {
      //   headerName: "",
      //   field: "actions",
      //   pinned: "right",
      //   hide: false,
      //   width: 70,
      //   maxWidth: 70,
      //   suppressMenu: true,
      //   filter: false,
      //   menuTabs: [],
      //   colId: "action-column",
      //   suppressNavigable: true,
      //   suppressColumnsToolPanel: true,
      //   // cellStyle: { "padding-top": "12px", "line-height": "18px" },
      //   resizable: false,
      //   sortable: false,
      //   cellRendererParams: ({ value }: { value: any }) => value?.props,
      //   // cellRenderer: SubmitButtonRender,
      //   cellRenderer: CTADropdown,
      // },
    ],
    [companyFiles],
  );

  const rowData = useMemo(
    () =>
      data?.map((item: any, index: number) => ({
        companyName: item.name,
        companyNameTooltip: item.name,
        cin: item.cin,
        cinTooltip: item.cin,
        role: item.role,
        isPublished: item.isOnboarded,
        status: item.status,
        authStrategy: item.authStrategy,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        tags: item.tags || [],
        suggestedPas3File: item.suggestedPas3File,
        suggestedPas3FileTooltip: item.suggestedPas3File,
        suggestedMgt7File: item.suggestedMgt7File,
        suggestedMgt7FileTooltip: item.suggestedMgt7File,
        publishedDate: item.publishDate
          ? formatDate(item.publishDate)
          : undefined,
        publishedCompanyId: item.publishedCompanyId,
        pas3: getFilesOfCin(item.cin).pas3
          ? `${item.cin}/${getFilesOfCin(item.cin).pas3}`
          : undefined,
        mgt7: getFilesOfCin(item.cin).mgt7
          ? `${item.cin}/${getFilesOfCin(item.cin).mgt7}`
          : undefined,
      })),
    [data, companyFiles],
  );

  const gridOptions = {
    suppressRowVirtualisation: true,
    paginationAutoPageSize: false,
    suppressScrollOnNewData: true,
  };

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    [],
  );

  const handleFileSelect = useCallback(
    (value: string) => {
      if (selectedCin) {
        if (isPas3File) {
          updateCompanyFile(selectedCin, "pas3", value);
        } else {
          updateCompanyFile(selectedCin, "mgt7", value);
        }
      }
    },
    [selectedCin, isPas3File, updateCompanyFile],
  );

  const rowSelection = useMemo<RowSelectionOptions>(() => {
    return {
      mode: "multiRow",
      // enableClickSelection: true,
    };
  }, []);

  const onFilterChanged = () => {
    const count = gridApi?.current?.api?.getDisplayedRowCount();
    setRowCount(count);
  };

  const handleQuickFilter = (e: any) => {
    const value = e.target.value;
    gridApi.current.api.setGridOption("quickFilterText", value);

    // Allow AG Grid time to apply the filter, then update row count
    setTimeout(() => {
      const count = gridApi.current.api.getDisplayedRowCount();
      setRowCount(count);
    }, 0); // or use requestAnimationFrame
  };

  function deselectAllRows() {
    gridApi?.current?.api?.deselectAll();
  }

  return (
    <div className="h-[calc(100vh-15vh)]">
      <div className="flex flex-row justify-between pt-4">
        <div>
          <h1 className="mt-4 text-xl text-secondary font-bold">Onboarding</h1>
          <p className="text-xs text-gray-500 font-semibold">
            {gridApi?.current?.api?.getDisplayedRowCount() || data?.length}{" "}
            Companies
          </p>
        </div>
        <div className="justify-end flex flex-row py-2 items-center gap-4">
          <button
            onClick={() => {
              const cins = selectedRowData?.map((item: any) => item.cin) || [];
              setIsPublishLoading(true);
              PublishBulkCompanies(cins, {
                onSuccess() {
                  toast("Publish request placed Successfully", {
                    autoClose: 2000,
                    type: "success",
                  });
                  deselectAllRows();
                },
                onError: (error: any) => {
                  toast(
                    error?.response?.data?.errorMessage ||
                      "Something went wrong",
                    {
                      type: "error",
                      autoClose: 2000,
                    },
                  );
                },
              });
            }}
            disabled={
              selectedRowData?.length === 0 ||
              findCompletedCompanies(selectedRowData)?.length > 0
            }
            className={`px-4 py-2 text-sm text-white ${selectedRowData?.length > 0 && selectedRowData && findCompletedCompanies(selectedRowData)?.length === 0 ? "bg-blue-500 hover:bg-blue-700" : "cursor-not-allowed bg-blue-300"} rounded flex-shrink-0 whitespace-nowrap`}
          >
            {isPublishLoading && (
              <LoaderText buttonSize={ButtonSize.SMALL} text={"PLease Wait"} />
            )}
            {!isPublishLoading &&
              `Publish ${selectedRowData ? ` (${selectedRowData?.length})` : ""}`}
          </button>

          <button
            onClick={() => {
              setIsLoading(true);
              GenerateBulkExcel(selectedRowData, {
                onSuccess() {
                  toast("Request Placed Successfully", {
                    autoClose: 2000,
                    type: "success",
                  });
                  deselectAllRows();
                },
                onError: (error: any) => {
                  toast(
                    error?.response?.data?.errorMessage ||
                      "Something went wrong",
                    {
                      type: "error",
                      autoClose: 2000,
                    },
                  );
                },
              });
            }}
            disabled={
              selectedRowData?.length === 0 ||
              findInvalidCompanies(selectedRowData)?.length > 0
            }
            className={`px-4 py-2 text-sm text-white ${selectedRowData?.length > 0 && selectedRowData && findInvalidCompanies(selectedRowData)?.length === 0 ? "bg-blue-500 hover:bg-blue-700" : "cursor-not-allowed bg-blue-300"} rounded flex-shrink-0 whitespace-nowrap`}
          >
            {isLoading && (
              <LoaderText buttonSize={ButtonSize.SMALL} text={"Generating"} />
            )}
            {!isLoading &&
              `Generate Excel${selectedRowData ? ` (${selectedRowData?.length})` : ""}`}
          </button>
          <QuickSearch
            placeholder={`Search`}
            className=""
            onChange={(e) => {
              handleQuickFilter(e);
              gridApi.current.api.setGridOption(
                "quickFilterText",
                e.target.value,
              );
            }}
          />
        </div>
      </div>
      {showFilesPopup && selectedCin && (
        <CompanyFilesPopup
          cin={selectedCin}
          showPopup={showFilesPopup}
          onClose={() => setShowFilesPopup(false)}
          onFileSelect={handleFileSelect}
        />
      )}
      {showCompanyDetailsPopup && selectedCin && (
        <CompanyDetailsPopup
          cin={selectedCin}
          showPopup={showCompanyDetailsPopup}
          onClose={() => setShowCompanyDetailsPopup(false)}
          onSave={() => {}}
          selectedPas3={
            data?.find((item: any) => item.cin === selectedCin)?.pas3 ??
            undefined
          }
        />
      )}
      {showShareholderPopup && selectedCin && (
        <ShareholderDetailsPopup
          cin={selectedCin}
          showPopup={showShareholderPopup}
          onClose={() => setShowShareholderPopup(false)}
          onSave={() => {}}
          selectedMgt7={
            data?.find((item: any) => item.cin === selectedCin)?.mgt7 ??
            undefined
          }
        />
      )}
      {showDocumentsPopup && selectedCin && (
        <FinalDocumentsPopup
          cin={selectedCin}
          showPopup={showDocumentsPopup}
          onClose={() => setShowDocumentsPopup(false)}
          selectedMgt7={
            data?.find((item: any) => item.cin === selectedCin)?.mgt7 ??
            undefined
          }
          selectedPas3={
            data?.find((item: any) => item.cin === selectedCin)?.pas3 ??
            undefined
          }
          onSave={() => {}}
        />
      )}
      <div className="ag-theme-alpine pb-4 flex mt-4" style={{ height: "90%" }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          components={componentsRegistry}
          ref={gridApi}
          onFilterChanged={onFilterChanged}
          cellSelection={true}
          pagination={true}
          enableCellTextSelection={true}
          paginationPageSize={20}
          onGridReady={(params) => {
            gridApi.current = params;
          }}
          rowSelection={rowSelection}
          onSelectionChanged={() => {
            const selectedNodes = gridApi.current?.api.getSelectedNodes();
            const selectedData = selectedNodes?.map((node: any) => node.data);
            setSelectedRowData(selectedData);
          }}
          alwaysMultiSort
          defaultColDef={defaultColDef}
          className="w-full col"
          sideBar={sideBar}
          gridOptions={gridOptions}
          getRowStyle={(params) => {
            if (params.rowIndex % 2 === 0) {
              return { background: "#f8f8f8" };
            } else {
              return { background: "#ffffff" };
            }
          }}
        />
      </div>
    </div>
  );
};
